/**
 * useViewport - Hook centralisé pour les décisions responsive.
 *
 * Breakpoints :
 *   phone   : < 768px
 *   tablet  : 768px - 1023px
 *   desktop : >= 1024px
 *
 * Le hook retourne la largeur courante et trois booléens.
 * Les composants utilisent isPhone/isTablet/isDesktop pour conditionner
 * leur rendu (drawer vs sidebar, cards vs table, etc).
 *
 * Implémentation : window.innerWidth + listener resize débouncé 100ms.
 * SSR-safe : retourne desktop par défaut si window absent.
 */

import { useState, useEffect } from 'react';

export interface Viewport {
  width: number;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const PHONE_MAX = 767;
const TABLET_MAX = 1023;

function getViewport(): Viewport {
  if (typeof window === 'undefined') {
    return { width: 1280, isPhone: false, isTablet: false, isDesktop: true };
  }
  const width = window.innerWidth;
  return {
    width,
    isPhone: width <= PHONE_MAX,
    isTablet: width > PHONE_MAX && width <= TABLET_MAX,
    isDesktop: width > TABLET_MAX,
  };
}

export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(getViewport);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setViewport(getViewport());
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return viewport;
}
